import { normalizeBrandSpelling } from './brand-spelling'
import { BRAND_RULES, SPECIAL_TERMS, isUnitInContext, unitCasing, unitSpacing } from '@/lib/brand-rules'
import { displayNameForCollectionSlugOrType } from '@/lib/collections'

/**
 * Brand names cache - loaded from Supabase brands table
 * This ensures brand names match exactly as stored in the database
 */
let brandNamesCache: Record<string, string> | null = null

/**
 * Load brand names from Supabase API
 */
async function loadBrandNames(): Promise<Record<string, string>> {
  if (brandNamesCache) return brandNamesCache

  try {
    const response = await fetch('/api/brands')
    if (response.ok) {
      const data = await response.json()
      const brands = data.brands || data // Handle both { brands: [...] } and [...] formats
      brandNamesCache = {}

      if (Array.isArray(brands)) {
        brands.forEach((brand: { name: string }) => {
          // Store lowercase key -> proper name
          brandNamesCache![brand.name.toLowerCase()] = brand.name
        })
      }

      return brandNamesCache
    }
  } catch (error) {
    // Silently handle - brand names will use slug fallback
  }

  return {}
}

/**
 * The hyphen-joined run of word characters that contains the token at
 * `offset` — "KOS-36RFD07T" for either of its halves, the token itself when it
 * stands alone. Used to keep a hyphenated model number whole when deciding
 * whether a token may be re-cased.
 */
function hyphenCompoundAround(text: string, offset: number, length: number): string {
  let start = offset
  let end = offset + length
  while (start >= 2 && text[start - 1] === '-' && /\w/.test(text[start - 2])) {
    start -= 1
    while (start > 0 && /\w/.test(text[start - 1])) start -= 1
  }
  while (end + 1 < text.length && text[end] === '-' && /\w/.test(text[end + 1])) {
    end += 1
    while (end < text.length && /\w/.test(text[end])) end += 1
  }
  return text.slice(start, end)
}

/** True when any hyphen-separated part mixes letters and digits ("36RFD07T"). */
function isModelLikeCompound(compound: string): boolean {
  return compound.split('-').some((part) => /[A-Za-z]/.test(part) && /\d/.test(part))
}

/**
 * Format text for breadcrumbs with proper capitalization
 * - Capitalizes first letter of every word
 * - Handles special brand/product terms with specific styling
 * - Syncs brand names from Supabase brands table
 * - See docs/Design.md for complete rules (438 brands)
 */
export function formatBreadcrumbText(text: string, brands?: Record<string, string>): string {
  if (!text) return ''

  // Merge all brand rules (from lib/brand-rules.ts - 438 brands)
  const allRules: Record<string, string> = {
    ...BRAND_RULES,
    ...SPECIAL_TERMS,
    ...(brands || {}), // Brand names from Supabase (if provided)
  }

  // Replace all brand rules first (case-insensitive)
  let formattedText = text
  Object.keys(allRules).forEach(key => {
    const regex = new RegExp(`\\b${key}\\b`, 'gi')
    formattedText = formattedText.replace(regex, allRules[key])
  })

  // Capitalize first letter of every word (that hasn't been replaced by brand rules)
  formattedText = formattedText.replace(/\b\w+/g, (word, offset: number, whole: string) => {
    // Check if this word is already a brand/special term (exact match)
    const isSpecialTerm = Object.values(allRules).some(
      term => term.toLowerCase() === word.toLowerCase()
    )

    // If it's a special term, keep it as is (already replaced above)
    if (isSpecialTerm) {
      return word
    }

    // A token that mixes letters and digits is a model number or a SKU
    // ("WRFC5036RZ", "SRFB27W3", "4K") — never a word to case. Title-casing it
    // printed "Wrfc5036rz" in the H1 while the <title> said "WRFC5036RZ"
    // (FRIDGE-348, live 08/17/2026). Such a token renders exactly as stored,
    // and so does every hyphen-joined part of it ("KOS-36RFD07T" keeps "KOS").
    // A plain number beside a word ("24-inch", "4-door") is not a model, so
    // those still title-case as before.
    if (isModelLikeCompound(hyphenCompoundAround(whole, offset, word.length))) {
      return word
    }

    // A unit of measure standing after a number is not a word to case
    // (FRIDGE-134 residual, live 08/17/2026: "21.9 Cuft", "45 Db",
    // "350 kWh/Yr", "Frigidaire 30 In. 20.0 Cu. Ft."). It renders in the
    // unit's own casing — "dB", "Hz", "cu ft" — and the number requirement is
    // what keeps the preposition in "refrigerator in black" title-cased while
    // the measurement in "30 in. wide" is not.
    if (isUnitInContext(word, offset, whole)) {
      // `unitSpacing` after `unitCasing`: casing first so "CUFT" and "Cuft"
      // both reach the spacing table by their lowercase key, then the unspaced
      // abbreviations the catalogue writes are aligned to the spaced form the
      // other 1,801 titles already use ("cuft" → "cu ft", FRIDGE-372). Both are
      // inside the unit-context branch, so a word merely containing the
      // sequence is never rewritten.
      return unitSpacing(unitCasing(word))
    }

    // Otherwise, capitalize first letter
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  // Fix apostrophe-s capitalization (e.g., "Year'S" → "Year's")
  // In English, the letter after an apostrophe in possessives/contractions is always lowercase
  formattedText = formattedText.replace(/'S\b/g, "'s")

  return normalizeBrandSpelling(formattedText)
}

/**
 * Format collection/category slug for breadcrumb display
 * Converts slug format (e.g., "french-door-refrigerator" or "mini_fridge") to title case
 * Handles both hyphens (URLs) and underscores (database)
 */
export function formatCollectionName(slug: string): string {
  if (!slug) return ''

  // Prefer canonical collection display name when we can resolve it
  const canonicalDisplay = displayNameForCollectionSlugOrType(slug)
  if (canonicalDisplay) return canonicalDisplay

  // Fallback: replace both hyphens AND underscores with spaces and format
  const withSpaces = slug.replace(/[-_]/g, ' ')
  return formatBreadcrumbText(withSpaces)
}

/**
 * Format product title with proper capitalization
 * Same rules as breadcrumbs - capitalize first letter of every word + special terms
 */
export function formatProductTitle(title: string, brands?: Record<string, string>): string {
  return formatBreadcrumbText(title, brands)
}

/**
 * Format blog title with proper capitalization
 * Same rules as breadcrumbs - capitalize first letter of every word + special terms
 */
export function formatBlogTitle(title: string, brands?: Record<string, string>): string {
  return formatBreadcrumbText(title, brands)
}

/**
 * Export the brand loading function for use in components
 */
export { loadBrandNames }

